import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';

interface ColorValue {
    hex: string;
    alpha: number;
}

const DEFAULT_COLOR: ColorValue = { hex: '#000000', alpha: 1 };
const HEX_PATTERN = /^#[\da-f]{3}(?:[\da-f]{3})?$/i;
const RGB_PATTERN = /^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i;

function parseColor(value: unknown): ColorValue | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim();
    const hexMatch = normalizedValue.match(HEX_PATTERN);

    if (hexMatch) {
        const shorthand = normalizedValue.length === 4;
        const hex = shorthand
            ? [...normalizedValue.slice(1)].map((character) => character.repeat(2)).join('')
            : normalizedValue.slice(1);

        return { hex: `#${hex.toUpperCase()}`, alpha: 1 };
    }

    const rgbMatch = normalizedValue.match(RGB_PATTERN);
    if (!rgbMatch) {
        return null;
    }

    const red = Number(rgbMatch[1]);
    const green = Number(rgbMatch[2]);
    const blue = Number(rgbMatch[3]);
    const alpha = rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]);

    if ([red, green, blue].some((channel) => channel > 255) || alpha > 1) {
        return null;
    }

    const hex = [red, green, blue]
        .map((channel) => channel.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();

    return { hex: `#${hex}`, alpha };
}

function formatColor({ hex, alpha }: ColorValue): string {
    if (alpha === 1) {
        return hex;
    }

    const channels = [1, 3, 5].map((position) => parseInt(hex.slice(position, position + 2), 16));
    const normalizedAlpha = Math.round(alpha * 100) / 100;

    return `rgba(${channels.join(', ')}, ${normalizedAlpha})`;
}

@Component({
    selector: 'app-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrls: ['./color-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ColorPickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ColorPickerComponent),
            multi: true,
        },
    ],
})
export class ColorPickerComponent implements ControlValueAccessor, Validator {
    @Input() label = '';

    private disabledByInput = false;
    private disabledByForm = false;

    @Input()
    set disabled(value: boolean) {
        this.disabledByInput = value;
    }

    get disabled(): boolean {
        return this.disabledByInput || this.disabledByForm;
    }

    @ViewChild('nativeColorPicker')
    private nativeColorPicker!: ElementRef<HTMLInputElement>;

    private color: ColorValue = { ...DEFAULT_COLOR };
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    textValue = DEFAULT_COLOR.hex;
    get hexColor(): string {
        return this.color.hex;
    }

    get alpha(): number {
        return this.color.alpha;
    }

    get colorValue(): string {
        return formatColor(this.color);
    }

    get alphaGradient(): string {
        return `linear-gradient(to right, transparent, ${this.hexColor})`;
    }

    writeValue(value: string | null): void {
        if (!value) {
            this.updateColor({ ...DEFAULT_COLOR });
            return;
        }

        const color = parseColor(value);
        if (color) {
            this.updateColor(color);
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabledByForm = isDisabled;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return !control.value || parseColor(control.value) ? null : { invalidColor: true };
    }

    openColorPicker(): void {
        this.nativeColorPicker.nativeElement.click();
    }

    onNativeColorInput(event: Event): void {
        const hex = (event.target as HTMLInputElement).value.toUpperCase();
        this.updateColor({ ...this.color, hex });
        this.emitValue();
    }

    onAlphaInput(event: Event): void {
        const alpha = Number((event.target as HTMLInputElement).value) / 100;
        this.updateColor({ ...this.color, alpha });
        this.emitValue();
    }

    onTextInput(event: Event): void {
        this.textValue = (event.target as HTMLInputElement).value;

        const color = parseColor(this.textValue);
        if (color) {
            this.color = color;
            this.emitValue();
        }
    }

    onTextEnter(event: Event): void {
        event.preventDefault();
        this.commitText();
    }

    onTextBlur(): void {
        this.commitText();
    }

    private commitText(): void {
        const color = parseColor(this.textValue);
        this.updateColor(color ?? this.color);
        this.onTouched();
    }

    private updateColor(color: ColorValue): void {
        this.color = color;
        this.textValue = formatColor(color);
    }

    private emitValue(): void {
        this.onChange(formatColor(this.color));
        this.onTouched();
    }
}
